import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar, Box, Button, Chip, Divider, IconButton,
  Input, List, ListItem, ListItemButton,
  Modal, ModalOverflow, ModalDialog, Sheet, Stack, Typography,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/joy';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

import AvatarWithStatus from '../MessagesChat/AvatarWithStatus';
import { MOTION, ellipsis, participantsLabel, thinScrollbar } from '../MessagesChat/chatTheme';
import { LayoutContext } from '../../context';
import { presenceSubtitle, usePresence } from '../../hooks/usePresence';
import { chatKindLabel, isDirectChat } from '../MessagesChat/directChat';
import ChatEventPreview from '../MessagesChat/ChatEventPreview';
import { useVisualViewport } from '../../hooks/useVisualViewport';
import {
  APIGetChatParticipants,
  APIRemoveUserFromChat,
  APIGetEmployeesNotInChat,
  APIAddUserToChat,
} from '../../api';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';


// ─── Позиции ────────────────────────────────────────────────────────
const POSITION_MAP = {
  surveyor:       { label: "Обмерщик",          color: "#6366f1" },
  draftsman:      { label: "Чертежник",          color: "#0ea5e9" },
  manager:        { label: "Менеджер",           color: "#10b981" },
  quality_master: { label: "Контроль качества",  color: "#f59e0b" },
  // head_manager и administrator раньше отсутствовали, а список чипов
  // отфильтрован по этому справочнику — должность просто пропадала из
  // карточки, хотя у человека она есть.
  head_manager:   { label: "Старший менеджер",   color: "#8b5cf6" },
  administrator:  { label: "Администратор",      color: "#ef4444" },
};

function getPositionChips(position) {
  if (!Array.isArray(position)) return null;
  return position
    .filter(p => POSITION_MAP[p])
    .map(p => (
      <Chip
        key={p}
        size="sm"
        variant="soft"
        sx={{
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: `${POSITION_MAP[p].color}18`,
          color: POSITION_MAP[p].color,
          '--Chip-minHeight': '18px',
        }}
      >
        {POSITION_MAP[p].label}
      </Chip>
    ));
}

function getRoleBadge(role) {
  if (role === 'creator') return (
    <Chip size="sm" variant="outlined" color="warning" sx={{ fontSize: '0.6rem', '--Chip-minHeight': '16px' }}>
      Создатель
    </Chip>
  );
  if (role === 'admin') return (
    <Chip size="sm" variant="outlined" color="primary" sx={{ fontSize: '0.6rem', '--Chip-minHeight': '16px' }}>
      Админ
    </Chip>
  );
  return null;
}


// ─── Секция добавления участника ────────────────────────────────────
function AddParticipantSection({ chatId, onAdded }) {
  const { tokenEmployee } = useContext(LayoutContext);
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && chatId) {
      loadEmployees();
    }
  }, [open, chatId]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await APIGetEmployeesNotInChat(chatId, tokenEmployee);
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Ошибка загрузки:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (employeeId) => {
    try {
      setLoading(true);
      await APIAddUserToChat(chatId, employeeId, tokenEmployee);
      onAdded();
      loadEmployees(); // Обновляем список
    } catch (error) {
      console.error('Ошибка добавления:', error);
      alert('Ошибка при добавлении пользователя');
    } finally {
      setLoading(false);
    }
  };

  const filtered = employees.filter(emp => {
    const fullName = `${emp.last_name || ''} ${emp.name || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  if (!open) {
    return (
      <Button
        variant="soft"
        color="primary"
        size="sm"
        startDecorator={<PersonAddAltRoundedIcon />}
        onClick={() => setOpen(true)}
        sx={{ borderRadius: 'lg', mx: 2, mb: 1 }}
      >
        Добавить участника
      </Button>
    );
  }

  return (
    <Sheet variant="soft" color="primary" sx={{ mx: 2, mb: 1, borderRadius: 'lg', p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography level="body-sm" fontWeight={600}>Добавить участника</Typography>
        <IconButton size="sm" variant="plain" onClick={() => { setOpen(false); setSearchTerm(''); }}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Input
        size="sm"
        placeholder="Поиск..."
        startDecorator={<SearchRoundedIcon sx={{ fontSize: 16 }} />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 1, borderRadius: 'lg' }}
      />
      <List sx={{ maxHeight: 200, overflow: 'auto', '--ListItem-paddingY': '4px' }}>
        {loading ? (
          <Typography level="body-xs" sx={{ textAlign: 'center', py: 2 }}>Загрузка...</Typography>
        ) : filtered.length === 0 ? (
          <Typography level="body-xs" sx={{ textAlign: 'center', py: 2 }}>Нет доступных сотрудников</Typography>
        ) : (
          filtered.map(emp => (
            <ListItem key={emp.id}>
              <ListItemButton
                onClick={() => handleAdd(emp.id)}
                disabled={loading}
                sx={{ borderRadius: 'lg', gap: 1.5 }}
              >
                <Avatar size="sm" sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>
                  {(emp.last_name?.[0] || '') + (emp.name?.[0] || '')}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography level="body-xs" fontWeight={600}>
                    {emp.last_name} {emp.name}
                  </Typography>
                </Box>
                <PersonAddAltRoundedIcon sx={{ fontSize: 16, color: 'primary.500' }} />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Sheet>
  );
}


// ─── Основной компонент ─────────────────────────────────────────────
export default function ModalChatInfo({ open, onClose, chatId, chat = null, onParticipantsChanged, presence = null }) {
  const { tokenEmployee, account, effectiveRole } = useContext(LayoutContext);
  const [infoResult, setInfoResult] = useState(null);
  const infoScope = `${chatId}:${tokenEmployee}`;
  const chatInfo = infoResult?.scope === infoScope ? infoResult.value : null;
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [eventPreview, setEventPreview] = useState(null);
  const loadEpoch = useRef(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // В карточке есть поиск по сотрудникам, то есть клавиатура здесь тоже
  // открывается. 100dvh её не учитывает, и нижняя часть списка уходит под
  // неё — та же история, что и в самом чате.
  const { height: vvHeight } = useVisualViewport();
  const mobileHeight = vvHeight ? `${vvHeight}px` : '100dvh';

  // Статусы обычно приходят готовыми из открытого чата — они там живые,
  // их обновляет веб-сокет. Карточку открывают и из мест, где чата рядом
  // нет (список, карточка события) — тогда грузим сами, но только пока
  // карточка открыта.
  const { presence: ownPresence } = usePresence(chatId, tokenEmployee, {
    enabled: open && !presence,
  });
  const livePresence = presence || ownPresence;

  const statusByKey = useMemo(() => {
    const map = {};
    (livePresence?.participants || []).forEach((p) => {
      if (p.kind && p.id != null) map[`${p.kind}:${p.id}`] = p;
    });
    return map;
  }, [livePresence]);

  // Может ли текущий пользователь управлять участниками
  const direct = isDirectChat(chatInfo || chat);
  const canManage = !direct && Boolean(chatInfo?.can_manage);

  const loadChatInfo = async () => {
    if (!chatId) return;
    const epoch = ++loadEpoch.current;
    try {
      setLoading(true);
      setLoadError('');
      const data = await APIGetChatParticipants(chatId, tokenEmployee);
      if (epoch !== loadEpoch.current) return;
      if (!Array.isArray(data?.participants)) throw new Error('Не удалось загрузить информацию о чате');
      setInfoResult({ scope: infoScope, value: data });
    } catch (error) {
      if (epoch !== loadEpoch.current) return;
      setInfoResult(null);
      setLoadError('Не удалось загрузить информацию. Проверьте подключение и доступ к чату.');
    } finally {
      if (epoch === loadEpoch.current) setLoading(false);
    }
  };

  useEffect(() => {
    setInfoResult(null); setLoadError(''); setEventPreview(null);
    if (open && chatId) {
      loadChatInfo();
    }
    return () => { loadEpoch.current += 1; };
  }, [open, chatId, tokenEmployee]);

  const handleRemoveUser = async (participantId, participantName) => {
    const confirmed = window.confirm(
      `Вы уверены, что хотите удалить "${participantName}" из чата?`
    );
    if (!confirmed) return;

    try {
      await APIRemoveUserFromChat(chatId, participantId, tokenEmployee);
      loadChatInfo(); // Обновляем список
      if (onParticipantsChanged) onParticipantsChanged();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      alert('Ошибка при удалении пользователя из чата');
    }
  };

  const handleParticipantAdded = () => {
    loadChatInfo();
    if (onParticipantsChanged) onParticipantsChanged();
  };

  const participants = chatInfo?.participants || [];

  return (
    <AnimatePresence>
      {open && (
        <Modal
          key="chat-info"
          open={open}
          onClose={onClose}
          sx={{ zIndex: 10200 }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: 'blur(0px)' },
              animate: { opacity: 1, backdropFilter: 'blur(8px)' },
              exit: { opacity: 0, backdropFilter: 'blur(0px)' },
              transition: { duration: 0.2, ease: "easeInOut" },
            }
          }}
        >
          <ModalOverflow sx={{ height: 'auto' }}>
            <ModalDialog
              layout={isMobile ? "fullscreen" : "center"}
              size="md"
              component={motion.div}
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              sx={{
                width: isMobile ? '100%' : 420,
                maxHeight: isMobile ? mobileHeight : '80vh',
                p: 1,
                borderRadius: { xs: 0, sm: '22px' },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // На десктопе центрируем флексом ModalOverflow, а не собственным
                // translate(-50%,-50%) Joy: framer-motion пишет свой transform из
                // animate и это translate затирает. На мобиле layout="fullscreen",
                // там позиционирование через inset и трогать его нельзя.
                ...(isMobile ? null : { position: 'relative', top: 'auto', left: 'auto', my: 'auto' }),
              }}
            >
              {/* ─── Шапка с аватаром ─── */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: 3,
                pb: 2,
                px: 2,
                position: 'relative',
              }}>
                <IconButton
                  variant="plain"
                  size="sm"
                  onClick={onClose}
                  sx={{
                    position: 'absolute', top: 10, right: 10, borderRadius: '50%',
                    '&:hover': { backgroundColor: 'background.level1' },
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>

                {/* Аватар общий с чатом: цвет выводится из названия, поэтому
                    в карточке, в шапке и в списке он одинаковый */}
                <Box sx={{ mb: 1.5 }}>
                  <AvatarWithStatus size="lg" name={chatInfo?.display_name || chatInfo?.chat_name || chat?.display_name || 'Чат'}
                    showStatus={direct && !!livePresence?.peer} online={!!livePresence?.peer?.online} />
                </Box>

                <Typography
                  level="title-md"
                  sx={{ fontWeight: 750, textAlign: 'center', px: 2, wordBreak: 'break-word' }}
                >
                  {chatInfo?.display_name || chatInfo?.chat_name || chat?.display_name || 'Чат'}
                </Typography>

                <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                  {direct ? presenceSubtitle(livePresence) || 'ЛС · Личный диалог' : participantsLabel(participants.length)}
                </Typography>
              </Box>

              <Divider />

              {loadError ? <Stack spacing={1} sx={{ p: 2 }}>
                <Typography level="body-sm" color="danger">{loadError}</Typography>
                <Button size="sm" variant="soft" onClick={loadChatInfo}>Повторить</Button>
              </Stack> : direct ? <DirectProfileDetails profile={chatInfo?.peer_profile} loading={loading} /> : <>

              {/* ─── Информация о чате ─── */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '10px',
                    bgcolor: '#572AC312', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, color: '#572AC3',
                  }}>
                    <ChatRoundedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 500 }}>
                      Тип чата
                    </Typography>
                    <Typography level="body-sm" fontWeight={600}>
                      {chatKindLabel(chatInfo)}
                    </Typography>
                  </Box>
                </Stack>

                {chatInfo?.date_create && (
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '10px',
                      bgcolor: '#572AC312', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0, color: '#572AC3',
                    }}>
                      <CalendarTodayRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 500 }}>
                        Дата создания
                      </Typography>
                      <Typography level="body-sm" fontWeight={600}>
                        {format(new Date(chatInfo.date_create), 'dd MMMM yyyy', { locale: ru })}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {chatInfo?.can_open_event && chatInfo?.event_id && (
                  <Button size="sm" variant="soft" fullWidth startDecorator={<OpenInNewRoundedIcon />}
                    onClick={() => setEventPreview({ scope: infoScope, id: chatInfo.event_id })} sx={{ mt: 1.5, borderRadius: '12px' }}>
                    Открыть событие #{chatInfo.event_id}
                  </Button>
                )}
              </Box>

              <Divider />

              {/* ─── Участники ─── */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  // Только вертикаль. Было overflow: 'auto' — то есть обе оси,
                  // и на телефоне список участников можно было таскать вбок:
                  // строка с должностями («Обмерщик · Менеджер · Чертежник»)
                  // не помещалась и распирала блок. Теперь чипы переносятся,
                  // а горизонтальная прокрутка запрещена совсем.
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  // Дотянув список до края, палец тянул за собой всю карточку.
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                  sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <GroupRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />
                    <Typography level="body-sm" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.tertiary' }}>
                      Участники
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {(livePresence?.online_count || 0) > 0 && (
                      <Chip size="sm" variant="soft" color="success"
                        sx={{ fontSize: '0.65rem', fontWeight: 700, '--Chip-minHeight': '20px' }}>
                        {livePresence.online_count} в сети
                      </Chip>
                    )}
                    <Chip size="sm" variant="soft" color="primary">
                      {participants.length}
                    </Chip>
                  </Stack>
                </Stack>

                {/* Кнопка добавления (только для менеджеров/quality_master) */}
                {canManage && (
                  <AddParticipantSection chatId={chatId} onAdded={handleParticipantAdded} />
                )}

                {loading ? (
                  <Typography level="body-sm" sx={{ textAlign: 'center', py: 4 }}>
                    Загрузка...
                  </Typography>
                ) : (
                  <List sx={{ '--ListItem-paddingY': '6px', px: 1, gap: 1 }}>
                    {participants.map((p) => {
                      const fullName = p.kind === 'client'
                        ? (p.name || 'Клиент')
                        : `${p.last_name || ''} ${p.name || ''}`.trim();
                      const isMe = p.is_me;
                      const canRemove = canManage && !isMe && p.role !== 'creator';
                      const st = statusByKey[`${p.kind}:${p.id}`];
                      const isOnline = !!st?.online;

                      return (
                        <ListItem key={`${p.kind}:${p.id}`}>
                          <ListItemButton
                            variant='outlined'
                            sx={{
                              borderRadius: 'lg',
                              gap: 1.5, py: 1.5,
                              cursor: 'default',
                              // minWidth: 0 обязателен: без него флекс-элемент
                              // не может стать уже своего содержимого, и именно
                              // он распирал строку вбок.
                              minWidth: 0,
                              alignItems: 'flex-start',
                              '&:hover': { bgcolor: 'background.level1' },
                            }}
                          >
                            {/* Свой статус не показываем: человек и так знает,
                                что он в сети, а серая точка на себе выглядит
                                как ошибка. */}
                            <AvatarWithStatus
                              size="sm"
                              name={fullName}
                              showStatus={!isMe && !!st}
                              online={isOnline}
                              sx={{ width: 36, height: 36, fontSize: '0.75rem' }}
                            />

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.25 }}
                              >
                                <Typography
                                  level="body-sm"
                                  fontWeight={600}
                                  noWrap
                                  sx={{ minWidth: 0, maxWidth: '100%' }}
                                >
                                  {fullName}
                                </Typography>
                                {isMe && (
                                  <Chip size="sm" variant="solid" color="primary"
                                    sx={{ fontSize: '0.6rem', '--Chip-minHeight': '16px' }}>
                                    вы
                                  </Chip>
                                )}
                                {getRoleBadge(p.role)}
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mt: 0.3, flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}
                              >
                                {p.kind === 'client' ? (
                                  <Chip size="sm" variant="soft" color="warning"
                                    sx={{ fontSize: '0.6rem', '--Chip-minHeight': '16px' }}>
                                    Клиент
                                  </Chip>
                                ) : (
                                  getPositionChips(p.position)
                                )}
                              </Stack>

                              {/* «в сети» / «был(а) вчера в 14:30» — подпись
                                  считает сервер, чтобы у всех она была одна
                                  и та же и не зависела от часов телефона. */}
                              {!isMe && st?.last_seen_text && (
                                <Typography
                                  level="body-xs"
                                  sx={{
                                    mt: 0.3, fontSize: '0.68rem',
                                    fontWeight: isOnline ? 650 : 500,
                                    color: isOnline ? 'success.500' : 'text.tertiary',
                                    transition: 'color .2s',
                                  }}
                                  noWrap
                                >
                                  {st.last_seen_text}
                                </Typography>
                              )}
                            </Box>

                            {canRemove && (
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveUser(p.id, fullName);
                                }}
                                sx={{ borderRadius: 'lg' }}
                              >
                                <PersonRemoveRoundedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
              </>}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
      {eventPreview?.scope === infoScope && <ChatEventPreview key={`event-preview:${eventPreview.id}`} open={open} eventId={eventPreview.id} token={tokenEmployee}
        onClose={() => setEventPreview(null)} />}
    </AnimatePresence>
  );
}

function DirectProfileDetails({ profile, loading }) {
  if (loading || !profile) return <Typography level="body-sm" sx={{ p: 3, textAlign: 'center' }}>
    {loading ? 'Загрузка профиля…' : 'Профиль собеседника недоступен'}
  </Typography>;
  const registered = profile.registered_at ? new Date(profile.registered_at) : null;
  const date = registered && !Number.isNaN(registered.getTime())
    ? format(registered, 'd MMMM yyyy', { locale: ru }) : null;
  const completed = Number.isFinite(profile.completed_tasks) ? profile.completed_tasks : null;
  const tenure = Number.isFinite(profile.tenure_days) && profile.tenure_days >= 0 ? profile.tenure_days : null;
  const reviews = Number.isFinite(profile.reviews_count) ? profile.reviews_count : 0;
  const rating = Number.isFinite(profile.rating) ? profile.rating.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : null;
  return <Stack spacing={2} sx={{ p: 2.25, minHeight: 0, overflowY: 'auto' }}>
    <Box>
      <Typography level="body-xs" sx={{ mb: 0.75, color: 'text.tertiary' }}>Должности</Typography>
      <Stack direction="row" sx={{ gap: 0.6, flexWrap: 'wrap' }}>
        {getPositionChips(profile.position)}
        {profile.role === 'administrator' && !profile.position?.includes('administrator') && getPositionChips(['administrator'])}
        {!profile.position?.length && profile.role !== 'administrator' && <Typography level="body-sm">Сотрудник</Typography>}
      </Stack>
    </Box>
    {date && <Stack direction="row" spacing={1} alignItems="center">
      <CalendarTodayRoundedIcon sx={{ fontSize: 19, color: 'primary.500' }} />
      <Box><Typography level="body-xs" sx={{ color: 'text.tertiary' }}>В системе</Typography>
        <Typography level="body-sm" sx={{ fontWeight: 600 }}>{tenure != null ? `${tenure.toLocaleString('ru-RU')} дн. · с ${date}` : `с ${date}`}</Typography>
      </Box>
    </Stack>}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
      <Sheet variant="soft" sx={{ p: 1.5, borderRadius: '14px' }}>
        <TaskAltRoundedIcon sx={{ color: 'primary.500', fontSize: 21, mb: 0.5 }} />
        <Typography level="title-lg" sx={{ fontWeight: 750 }}>{completed ?? '—'}</Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Выполнено задач</Typography>
      </Sheet>
      <Sheet variant="soft" sx={{ p: 1.5, borderRadius: '14px' }}>
        <StarRoundedIcon sx={{ color: 'warning.500', fontSize: 21, mb: 0.5 }} />
        <Typography level="title-lg" sx={{ fontWeight: 750 }}>{rating || '—'}</Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{reviews ? `Отзывов: ${reviews}` : 'Пока нет оценок'}</Typography>
      </Sheet>
    </Box>
    <Typography level="body-xs" sx={{ color: 'text.tertiary', lineHeight: 1.5 }}>
      Личный диалог между вами и собеседником.
    </Typography>
  </Stack>;
}
